import React, { useState } from "react";
import HelmetWrapper from "../HelmetWrapper";
import "./ErrorPages.css";

// Artwork is inline SVG + CSS, never a Lottie/CDN asset. This screen only ever
// appears when the network is gone, so anything that has to be fetched — even a
// bundled JSON behind a code-split chunk the browser has not downloaded yet —
// risks rendering an empty box at the one moment it has to work.
const WifiOff = () => (
  <svg className="errp-wifi" viewBox="0 0 120 120" fill="none" aria-hidden="true">
    <g stroke="#5B3DF5" strokeWidth="7" strokeLinecap="round">
      <path className="errp-wifi__arc errp-wifi__arc--3" d="M14 50a65 65 0 0 1 92 0" />
      <path className="errp-wifi__arc errp-wifi__arc--2" d="M31 68a41 41 0 0 1 58 0" />
      <path className="errp-wifi__arc errp-wifi__arc--1" d="M46 85a18 18 0 0 1 28 0" />
    </g>
    <circle className="errp-wifi__dot" cx="60" cy="96" r="6.5" fill="#5B3DF5" />
    <path className="errp-wifi__slash" d="M26 26 94 94" stroke="#EF4444" strokeWidth="7" strokeLinecap="round" />
  </svg>
);

const Offline = () => {
  // `navigator.onLine` only tells you the OS has *a* connection — a captive
  // portal or a dead router still reports true — so the retry actually re-fetches
  // rather than trusting the flag alone.
  const [checking, setChecking] = useState(false);
  const [failedAt, setFailedAt] = useState(null);

  // No "online" listener here on purpose. App.js already watches that event and
  // swaps this screen back out for the router in place — which restores the exact
  // page the user was on, with their form state intact. A window.location.reload()
  // here would fight that: it would throw away everything they had typed and, on a
  // deep page, is a far heavier way to arrive at the same URL. Reconnecting should
  // return you to the page you lost, not to a dashboard you didn't ask for.

  const retry = async () => {
    setChecking(true);
    setFailedAt(null);
    try {
      // cache:no-store so a service-worker/browser cache hit can't masquerade as
      // a live network. Same-origin, so this works without CORS.
      await fetch(`${window.location.origin}/favicon.ico?_=${Date.now()}`, { cache: "no-store" });
      window.location.reload();
    } catch (e) {
      setFailedAt(new Date().toLocaleTimeString());
    } finally {
      setChecking(false);
    }
  };

  return (
    <HelmetWrapper title="WTF Go — You’re offline">
      <div className="errp">
        <WifiOff />

        <h1 className="errp__title">You’re offline</h1>
        <p className="errp__sub">
          We can’t reach WTF Go right now. Check your Wi-Fi or mobile data —
          this page will come back on its own as soon as you’re connected.
        </p>

        <div className="errp__actions">
          <button type="button" className="errp__btn errp__btn--primary" onClick={retry} disabled={checking}>
            {checking ? "Checking…" : "Try again"}
          </button>
        </div>

        <div className="errp__hint">
          {failedAt
            ? `Still no connection (last checked ${failedAt}).`
            : "Nothing you had open is lost — reconnecting picks up where you left off."}
        </div>
      </div>
    </HelmetWrapper>
  );
};

export default Offline;
